import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import AdminHeader from '../../components/admin/AdminHeader';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { adminAPI } from '../../lib/api';

const RESULT_COLORS = {
  '0': 'bg-green-100 text-green-800',
  '': 'bg-gray-100 text-gray-700'
};

function resultBadgeClass(code) {
  if (code === '0') return RESULT_COLORS['0'];
  if (!code) return RESULT_COLORS[''];
  return 'bg-red-100 text-red-800';
}

const FamilyBankIpnPage = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [resultCode, setResultCode] = useState('');
  const [transactionType, setTransactionType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchRecords = async (page = 1) => {
    setLoading(true);
    try {
      const res = await adminAPI.getFamilyBankIpnRecords({
        page,
        limit: 25,
        search: debouncedSearch,
        resultCode,
        transactionType,
        dateFrom,
        dateTo
      });
      if (res?.success) {
        setRecords(res.data.records || []);
        setPagination(res.data.pagination || pagination);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load IPN records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, resultCode, transactionType, dateFrom, dateTo]);

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return `Ksh ${Number(amount).toLocaleString('en-KE')}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="lg:pl-72">
        <AdminHeader onMenuClick={() => setSidebarOpen(true)} title="Family Bank IPN" />

        <main className="p-6 max-w-7xl mx-auto">
          <div className="mb-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-600 mb-2">
              Super admin · Audit log
            </p>
            <h1 className="text-2xl font-bold text-gray-900">Family Bank IPN records</h1>
            <p className="text-sm text-gray-600 mt-1 max-w-3xl">
              STK callbacks and C2B IPN from Family Bank. Audit/reconciliation only - ticket fulfillment runs from the STK handler, not this table.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-6">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search trans ID, bill ref, phone…"
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm lg:col-span-2"
              />
              <select
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value)}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
              >
                <option value="">All types</option>
                <option value="STK Push">STK Push</option>
                <option value="Pay Bill">Pay Bill</option>
              </select>
              <select
                value={resultCode}
                onChange={(e) => setResultCode(e.target.value)}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
              >
                <option value="">All results</option>
                <option value="0">0 - Success</option>
                <option value="1">1 - Failed</option>
              </select>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                aria-label="From date"
              />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                aria-label="To date"
              />
            </div>
            <p className="text-xs text-gray-500 mt-3">
              {pagination.totalRecords} record{pagination.totalRecords !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-12 text-center text-gray-500">Loading IPN records…</div>
            ) : records.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                No IPN records yet. They appear when Family Bank posts to your IPN callback URL.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Received</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Trans ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Bill ref</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Phone</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Result</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Payload</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {records.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50/80">
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                          {new Date(row.receivedAt).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-800">{row.transId || '-'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-800">{row.billRefNumber || '-'}</td>
                        <td className="px-4 py-3 text-gray-700">{row.msisdn || '-'}</td>
                        <td className="px-4 py-3 text-gray-900 font-medium">{formatAmount(row.amount)}</td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{row.transactionType || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-semibold ${resultBadgeClass(row.resultCode)}`}>
                            {row.resultCode ?? '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => setSelectedRecord(row)}
                            className="text-orange-600 hover:text-orange-800 font-medium text-xs"
                          >
                            View JSON
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
                <button
                  type="button"
                  disabled={pagination.currentPage <= 1}
                  onClick={() => fetchRecords(pagination.currentPage - 1)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-40"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <button
                  type="button"
                  disabled={pagination.currentPage >= pagination.totalPages}
                  onClick={() => fetchRecords(pagination.currentPage + 1)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </main>
      </div>

      {selectedRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setSelectedRecord(null)}>
          <div
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">IPN payload</h2>
              <button type="button" onClick={() => setSelectedRecord(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <pre className="p-5 text-xs overflow-auto max-h-[60vh] bg-gray-50 text-gray-800">
              {JSON.stringify(selectedRecord.payload, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyBankIpnPage;
